import { type ReactNode, useCallback } from "react";
import {
  type AirCfarExerciseQuote,
  AirCfarExerciseRefundPaymentType,
  type AirCfarExerciseRefundPaymentTypeOpaqueValue,
} from "@b2bportal/air-cfar-api";
import { useCfarStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreFlightsCfarExerciseComponents } from "@b2bportal/core-types";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";

import defaultStyles from "./AirCfarExerciseConfirmCancellation.module.scss";

export interface AirCfarExerciseConfirmCancellationProps {
  itinerarySummary: ReactNode;
  quote: AirCfarExerciseQuote;
  onConfirmCancellationClick: () => void;
  onGoBackClick: () => void;
  onContactSupportClick: () => void;
}

export const AirCfarExerciseConfirmCancellation = ({
  itinerarySummary,
  quote,
  onConfirmCancellationClick,
  onGoBackClick,
  onContactSupportClick,
}: AirCfarExerciseConfirmCancellationProps) => {
  const [block, cn] = useModuleBEM(
    useCfarStyles(
      CoreFlightsCfarExerciseComponents.AirCfarExerciseConfirmCancellation,
      defaultStyles
    ),
    CoreFlightsCfarExerciseComponents.AirCfarExerciseConfirmCancellation
  );
  const { t, formatFiatCurrency, formatRewardsCurrency } = useI18nContext();

  const getRefundBreakdown = useCallback(
    (refundPayments: Array<AirCfarExerciseRefundPaymentTypeOpaqueValue>) => {
      let cashSegment = "";
      let rewardsSegment = "";
      let travelCreditSegment = "";
      refundPayments.forEach((payment) => {
        switch (payment.type) {
          case AirCfarExerciseRefundPaymentType.Cash: {
            const fiat = payment.value.amount?.fiat;
            cashSegment = formatFiatCurrency(fiat, {
              signDisplay: "never",
            });
            break;
          }
          /*  map CommbankRewards and PartnerRewards to the same thing,
            once this is done and deployed to production for CBA,
            BE will stop sending CommbankRewards and deprecate it */
          case AirCfarExerciseRefundPaymentType.CommbankRewards:
          case AirCfarExerciseRefundPaymentType.PartnerRewards: {
            const rewards = payment.value.amount?.rewards;
            const rewardsKey = Object.keys(rewards)[0];
            const rewardsAmount = rewards[rewardsKey];
            rewardsSegment = t(
              "core-flights-cfar.exercise.confirmCancellation.refundBreakdownRewardSegment",
              {
                rewardsAmount: formatRewardsCurrency(rewardsAmount),
              }
            );
            break;
          }
          case AirCfarExerciseRefundPaymentType.TravelWalletCredit:
            {
              const travelCreditAmount = payment.value.amount?.fiat
                ? formatFiatCurrency(payment.value.amount?.fiat, {
                    signDisplay: "never",
                  })
                : undefined;
              travelCreditSegment = t(
                "core-flights-cfar.exercise.confirmCancellation.refundBreakdownCreditSegment",
                {
                  travelCreditAmount,
                }
              );
            }
            break;
          default:
            break;
        }
      });
      const validRefundSegments = [
        cashSegment,
        rewardsSegment,
        travelCreditSegment,
      ].filter((segment) => !!segment);
      switch (validRefundSegments.length) {
        case 3:
          return t(
            "core-flights-cfar.exercise.confirmCancellation.refundBreakdownThreeItems",
            {
              refundSegment1: validRefundSegments[0],
              refundSegment2: validRefundSegments[1],
              refundSegment3: validRefundSegments[2],
            }
          );
        case 2:
          return t(
            "core-flights-cfar.exercise.confirmCancellation.refundBreakdownTwoItems",
            {
              refundSegment1: validRefundSegments[0],
              refundSegment2: validRefundSegments[1],
            }
          );
        case 1:
          return validRefundSegments[0];
        default:
          return "";
      }
    },
    [formatFiatCurrency, t, formatRewardsCurrency]
  );

  const refundPayments = quote.refundPayments;
  const refundString = getRefundBreakdown(refundPayments);

  const footerSuffix = t(
    "core-flights-cfar.exercise.confirmCancellation.footerSuffix",
    {
      defaultValue: "",
    }
  );

  const importantInformationPointFour = t(
    "core-flights-cfar.exercise.confirmCancellation.importantInformationPointFour",
    { defaultValue: "" }
  );

  return (
    <div className={block}>
      <div className={cn("body")}>
        <div className={cn("header")}>
          <div className={cn("header-title")}>
            {t("core-flights-cfar.exercise.confirmCancellation.title")}
          </div>
          <div className={cn("header-description")}>
            <Trans
              i18nKey="core-flights-cfar.exercise.confirmCancellation.description"
              values={{ refundString }}
              components={{
                strong: <strong className={cn("strong")} />,
              }}
            />
          </div>
        </div>
        <div className={cn("important-information")}>
          <div className={cn("important-information-title")}>
            {t(
              "core-flights-cfar.exercise.confirmCancellation.importantInformationTitle"
            )}
          </div>
          <div className={cn("important-information-points")}>
            <ul>
              <li>
                {t(
                  "core-flights-cfar.exercise.confirmCancellation.importantInformationPointOne"
                )}
              </li>
              <li>
                {t(
                  "core-flights-cfar.exercise.confirmCancellation.importantInformationPointTwo"
                )}
              </li>
              <li>
                {t(
                  "core-flights-cfar.exercise.confirmCancellation.importantInformationPointThree"
                )}
              </li>

              {importantInformationPointFour && (
                <li>{importantInformationPointFour}</li>
              )}

              <li>
                <Trans i18nKey="core-flights-cfar.exercise.confirmCancellation.importantInformationPointFive">
                  <span />
                  <span
                    className={cn("contact-support-link")}
                    onClick={onContactSupportClick}
                  />
                </Trans>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className={cn("sidebar")}>
        {itinerarySummary && (
          <div className={cn("itinerary-summary")}>{itinerarySummary}</div>
        )}
        <div className={cn("footer")}>
          <button
            className={cn("confirm-cancellation-button")}
            onClick={() => {
              onConfirmCancellationClick();
            }}
          >
            {t(
              "core-flights-cfar.exercise.confirmCancellation.confirmCancellation"
            )}
          </button>
          <button className={cn("go-back-button")} onClick={onGoBackClick}>
            {t("core-flights-cfar.exercise.confirmCancellation.goBack")}
          </button>
          {footerSuffix && (
            <div className={cn("footer-suffix")}>{footerSuffix}</div>
          )}
        </div>
      </div>
    </div>
  );
};
